import { z } from 'zod';
import { zodResolver } from '@hookform/resolvers/zod';
import { useForm } from 'react-hook-form';
import { useState } from 'react';
import { useDropzone } from 'react-dropzone';
import { ImagePlus, Loader2, X } from 'lucide-react';
import { MAX_SCREENSHOT_BYTES, SCREENSHOT_TYPES } from '../_enums';
import { Error } from '@/components/ui/error';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from '@/components/ui/form';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { useToast } from '@/helpers/toast';

import { useUploadZealScreenshot } from '@/data/zealTask';

const formSchema = z.object({
  title: z.string().trim().min(1, 'Title is required').max(200),
  description: z.string().max(20000),
  screenshots: z
    .array(z.instanceof(File))
    .refine((files) => files.every((file) => file.type in SCREENSHOT_TYPES), 'Screenshots must be PNG, JPEG or WebP')
    .refine((files) => files.every((file) => file.size <= MAX_SCREENSHOT_BYTES), 'Each screenshot must be 10 MB or less')
});

// Files an idea or a bug report: `onCreate` creates it, then each screenshot is
// uploaded against `getTarget(created)`. Once filed it cannot be changed here.
const FileItemDialog = ({ title, description, submitLabel, onCreate, getTarget, onClose, onSuccess }) => {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [apiError, setApiError] = useState('');
  const uploadScreenshot = useUploadZealScreenshot();
  const { toast } = useToast();

  const form = useForm({
    resolver: zodResolver(formSchema),
    defaultValues: {
      title: '',
      description: '',
      screenshots: []
    }
  });
  const screenshots = form.watch('screenshots');

  const dropzone = useDropzone({
    accept: SCREENSHOT_TYPES,
    maxSize: MAX_SCREENSHOT_BYTES,
    onDrop: (accepted, rejected) => {
      form.setValue('screenshots', [...form.getValues('screenshots'), ...accepted], { shouldValidate: true });
      if (rejected.length > 0) {
        form.setError('screenshots', { message: `Not added: ${rejected.map((r) => r.file.name).join(', ')}. Use PNG, JPEG or WebP, 10 MB or less.` });
      }
    }
  });

  const handleRemoveScreenshot = (index) => {
    form.setValue(
      'screenshots',
      form.getValues('screenshots').filter((_, i) => i !== index),
      { shouldValidate: true }
    );
  };

  const onSubmit = async (values) => {
    setApiError('');
    setIsSubmitting(true);
    let created;
    try {
      created = await onCreate({ title: values.title, description: values.description });
    } catch (err) {
      setIsSubmitting(false);
      setApiError(err.message);
      return;
    }

    // The item exists now, so a failed upload must not invite a second submit.
    const failed = await values.screenshots.reduce(async (previous, file) => {
      const names = await previous;
      try {
        await uploadScreenshot(getTarget(created), file);
        return names;
      } catch {
        return [...names, file.name];
      }
    }, Promise.resolve([]));

    await onSuccess();
    if (failed.length > 0) {
      toast({ variant: 'destructive', title: `${created.title} was filed`, description: `These screenshots failed to upload: ${failed.join(', ')}` });
    } else {
      toast({ title: `${created.title} was filed` });
    }
    onClose();
  };

  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent className="max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>{title}</DialogTitle>
          <DialogDescription>{description}</DialogDescription>
        </DialogHeader>
        <Form {...form}>
          <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-4">
            <FormField
              control={form.control}
              name="title"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Title</FormLabel>
                  <FormControl>
                    <Input {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            <FormField
              control={form.control}
              name="description"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Description</FormLabel>
                  <FormControl>
                    <Textarea rows={6} {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            <FormField
              control={form.control}
              name="screenshots"
              render={() => (
                <FormItem>
                  <FormLabel>Screenshots</FormLabel>
                  <div
                    {...dropzone.getRootProps()}
                    className="flex cursor-pointer flex-col items-center justify-center gap-1 rounded-md border border-dashed p-4 text-sm text-muted-foreground hover:bg-muted/50"
                  >
                    <input {...dropzone.getInputProps()} data-testid="screenshot-input" />
                    <ImagePlus className="h-5 w-5" />
                    <div>Drop images here, or click to choose</div>
                    <div className="text-xs">PNG, JPEG or WebP, up to 10 MB each</div>
                  </div>
                  {screenshots.length > 0 && (
                    <ul className="space-y-1">
                      {screenshots.map((file, index) => (
                        <li key={`${file.name}-${index}`} className="flex items-center justify-between gap-2 rounded border px-2 py-1 text-sm">
                          <span className="truncate">{file.name}</span>
                          <Button type="button" variant="ghost" size="icon" className="h-6 w-6" onClick={() => handleRemoveScreenshot(index)} disabled={isSubmitting}>
                            <X className="h-4 w-4" />
                            <span className="sr-only">Remove {file.name}</span>
                          </Button>
                        </li>
                      ))}
                    </ul>
                  )}
                  <FormMessage />
                </FormItem>
              )}
            />
            {apiError && <Error message={apiError} />}
            <DialogFooter>
              <Button type="submit" disabled={isSubmitting}>
                {isSubmitting && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                {submitLabel}
              </Button>
            </DialogFooter>
          </form>
        </Form>
      </DialogContent>
    </Dialog>
  );
};

export default FileItemDialog;
